<script setup lang="ts">
import { rewardImageMap } from '@/config/reward-images'
import { PATHWAY_EVENT_STEP_COST_TYPE, type Step, type StepReward } from '@/types/shared/pathway-event'
import { computed } from 'vue'
import CheckIconThin from '@/components/icons/CheckIconThin.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { twMerge } from 'tailwind-merge'
import LockIcon from '@/components/icons/LockIcon.vue'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { usePlayer } from '@/composables/game/player'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'

const props = withDefaults(defineProps<{
	eventId: string
	mainColor: string
	step: Step
	multiRewardSupport?: boolean
	isDone?: boolean
	isClaimable?: boolean
	arrowClass?: string
	hasArrow?: boolean
	onlyVisual?: boolean
	innerContainerClass?: string
	originalIndex?: number
	isLoading?: boolean
}>(), {
  multiRewardSupport: false,
  isDone: false,
  isClaimable: false,
  arrowClass: '',
  hasArrow: true,
  onlyVisual: false,
  innerContainerClass: '',
  originalIndex: undefined,
  isLoading: false,
})

const emit = defineEmits<{
	(e: 'onPurchase'): void
	(e: 'onPay'): void
	(e: 'outOfSpecialCurrency', currencyId: string): void
}>()

const { goldenCoinValue, player } = usePlayer()

const playerSpecialCurrencies = computed(() => player.value.specialCurrencies)

const visibleRewards = computed(() => {
  const { reward } = props.step
  const keys = (props.multiRewardSupport
    ? Object.keys(reward)
    : Object.keys(reward).slice(0, 1)) as (keyof StepReward)[]

  const result: (Exclude<keyof StepReward, 'specialCurrencies'> | string)[] = []

  for (const key of keys) {
    const value = reward[key as keyof StepReward]

    if (key === 'specialCurrencies' && value && typeof value === 'object') {
      const specialRewards = Object.entries(value)
        .filter(([, amount]) => amount != null)
        .map(([currencyId]) => currencyId)

      result.push(...specialRewards)
    } else if (value != null) {
      result.push(key as Exclude<keyof StepReward, 'specialCurrencies'>)
    }
  }

  return result
})

const outerContainerClass = computed(() => {
  if (props.isDone) {
    return 'bg-white/[.38]'
  }
  if (props.isClaimable) {
    return 'claimable-background'
  }
  return 'gold-background'
})

const stepCostLabel = computed(() => {
  if (props.step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.FREE) {
    return 'FREE'
  }
  if (props.step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.STAR) {
    return `$${props.step.cost.usdAmount?.toFixed(2)}`
  }
  return props.step.cost.amount
})

function isSpecialCurrencyId(value: string): boolean {
  return value in allSpecialCurrenciesConfig.value
}

function getRewardAmount(rewardType: Exclude<keyof StepReward, 'specialCurrencies'> | string) {
  if (rewardType === 'goldenCoins') {
    return formatNumberFullTruncate((props.step.reward[rewardType] as number) * goldenCoinValue.value)
  }
  if (isSpecialCurrencyId(rewardType)) {
    return formatNumberFullTruncate((props.step.reward.specialCurrencies as Record<string, number>)?.[rewardType] ?? 0)
  }
  return formatNumberFullTruncate(props.step.reward[rewardType as keyof StepReward] as number)
}

async function onStepClick() {
  if (!props.isClaimable || props.originalIndex === undefined) return
  if (props.step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.STAR) {
    emit('onPurchase')
    return
  }
  if (props.step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.SPECIAL_CURRENCY && props.step.cost.amount > (playerSpecialCurrencies.value[props.step.cost.currencyId] ?? 0)) {
    emit('outOfSpecialCurrency', props.step.cost.currencyId)
    return
  }
  emit('onPay')
}
</script>

<template>
  <div class="w-full h-[88px] rounded-xl relative">
    <div
      v-if="hasArrow"
      :class="twMerge('absolute top-1/2 -translate-y-1/2 w-12 h-auto -right-[40px] z-[3]', arrowClass)"
    >
      <slot
        v-if="$slots.arrowIcon"
        name="arrowIcon"
      />
      <img
        v-else
        class="object-cover w-full h-full"
        src="@/assets/images/pathway-right-arrow.png"
      >
    </div>
    <div
      class="w-full h-full rounded-xl shadow-container"
      :class="{
        'pointer-events-none': !isClaimable || onlyVisual || isLoading,
        'pulse-claimable claimable-shadow': !isDone && isClaimable && !onlyVisual,
        'claimable-shadow-visual': !isDone && isClaimable && onlyVisual,
      }"
      :style="{backgroundColor: isDone ? '#1C1C1E' : mainColor}"
      @click="onStepClick"
    >
      <div
        class="w-full h-full rounded-xl"
        :class="[isDone ? 'p-0.5' : 'p-[3px]', outerContainerClass]"
      >
        <div
          :class="twMerge('w-full h-full flex flex-col items-center justify-between px-[3px] pt-[7px] rounded-[9px]', isDone && 'pt-2', innerContainerClass)"
          :style="{backgroundColor: isDone ? '#1C1C1E' : mainColor}"
        >
          <div
            class="flex items-center gap-x-6 z-[1]"
            :class="{'px-6': multiRewardSupport}"
          >
            <div
              v-for="(rewardType, _index) in visibleRewards"
              :key="_index"
              class="flex flex-col items-center relative"
            >
              <div class="h-[38px] w-auto flex items-center justify-center">
                <img
                  class="w-auto h-full image-shadow"
                  :src="rewardImageMap[rewardType as keyof typeof rewardImageMap]"
                >
              </div>
              <span
                class="absolute left-1/2 -translate-x-1/2 black-stroke text-lg leading-5 font-bold -bottom-2 text-white"
              >{{ getRewardAmount(rewardType) }}</span>
            </div>
          </div>
          <div
            v-if="!onlyVisual"
            class="pb-px w-full flex items-center justify-center"
          >
            <CheckIconThin
              v-if="isDone"
              class="size-[66px] -translate-y-6 text-teal-primary z-[0]"
            />
            <BaseButton
              v-else-if="isClaimable"
              class="w-full mb-[3px]"
              button-class="h-6 p-0 rounded-[6px] gap-x-0 text-base leading-5 font-bold"
              style-type="primary"
              spinner-class="min-w-[12px] w-3 h-3"
              :loading="isLoading"
            >
              <div
                v-if="!isLoading"
                class="flex items-center"
              >
                <img
                  v-if="step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.SPECIAL_CURRENCY"
                  class="h-4 w-auto image-shadow object-cover mr-0.5"
                  :src="rewardImageMap[step.cost.currencyId]"
                >
                {{ stepCostLabel }}
              </div>
            </BaseButton>
            <div
              v-else
              class="flex items-center justify-center w-full mb-[4px]"
            >
              <LockIcon class="text-white/[.88] size-4 mr-0.5" />
              <img
                v-if="step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.SPECIAL_CURRENCY"
                class="h-4 w-auto image-shadow object-cover mr-0.5"
                :src="rewardImageMap[step.cost.currencyId]"
              >
              <span class="text-white text-sm leading-5 font-bold">{{ stepCostLabel }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shadow-container {
	box-shadow: 0px 0px 20px 0px #000;
}

.gold-background {
	background: linear-gradient(
		to right,
		#fff246 0%,
		#e5b80b 37%,
		#fff571 69%,
		#e5b80b 100%
	);
}

.claimable-background {
	background: linear-gradient(
		-45deg,
		rgba(237, 255, 202, 0.22) 13%,
			/* #EDFFCA at 22% opacity */ rgba(215, 115, 215, 1) 24%,
			/* #48650C at 100% opacity */ rgba(20, 241, 255, 1) 42%,
			/* #14F1FF at 100% opacity */ rgba(20, 241, 255, 1) 63%,
			/* #14F1FF at 100% opacity (same color) */ rgba(254, 78, 254, 0.77) 79%,
			/* #EEFFCA at 77% opacity */ rgba(215, 115, 215, 1) 82% /* #E2FFA7 at 100% opacity */
	);
}

.black-stroke {
	text-shadow: -1px -1px 0 #000000,
	1px -1px 0 #000000,
	-1px 1px 0 #000000,
	1px 1px 0 #000000,
	-1.5px 0 0 #000000,
	1.5px 0 0 #000000,
	0 -1.5px 0 #000000,
	0 1.5px 0 #000000,
	-0.5px -0.5px 0 #000000, /* Finer shadow adjustments */ 0.5px -0.5px 0 #000000,
	-0.5px 0.5px 0 #000000,
	0.5px 0.5px 0 #000000;
}

.image-shadow {
	filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.66));
}

.claimable-shadow {
	filter: drop-shadow(0px 0px 12px rgba(20, 241, 255, 0.88)); /* Full shadow */
}

.claimable-shadow-visual {
	filter: drop-shadow(0px 0px 3px rgba(20, 241, 255, 0.88)); /* Full shadow */
}

.pulse-claimable {
	animation: pulse-step 1s infinite linear;
	will-change: transform;
}

@keyframes pulse-step {
	0% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
	100% {
		transform: scale(1);
	}
}
</style>